import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { listDocumentAnnotationSets } from "../../../../api/content/documentAnnotationsApi";
import type { SourceDocument } from "../../../../api/content/sourcesApi";
import { listAcceptedStatements, listDocumentResults } from "../../../../api/knowledgeData/knowledgeDataApi";
import { THREAD_LIST_LIMIT } from "../../../../api/threads/threadsApi";
import type { AnnotationSet } from "../../../../api/threads/liveMessages";
import { isAllowed } from "../../../../api/access/decisions";
import { chosenProjectDecision, useProjects } from "../../../../projects/projectContext";
import { formatTimeAgo } from "../../../../shared/formatting/relativeTimes";
import { useLoad } from "../../../../shared/loading/useLoad";
import { formatDocumentVersionRef } from "../../../../shared/references/contentReferences";
import { appPaths } from "../../../../shared/routing/appPaths";
import { WorkspaceLink } from "../../../../shell/workspace";
import AppButton from "../../../../ui/buttons/AppButton";
import LoadStatus from "../../../../ui/feedback/LoadStatus";
import { FORM_SELECT_CLASS } from "../../../../ui/styles/formStyles";
import type { AnnotationLabelOption } from "../../../annotation/annotationLabels";
import { groupByDocument } from "../../../annotation/annotationPlacement";
import { useLabelListNames } from "../../../annotation/useLabelListNames";
import { AskInChatButton, SelectionToChat } from "../../../chatReferences";
import CurationStateBadge from "../../../knowledgeData/extractions/CurationStateBadge";
import { proposalPath } from "../../../knowledgeData/extractions/proposalPath";
import OpenReview from "../../../knowledgeData/results/OpenReview";
import { useLiveProject } from "../../../threads/live/threadLiveContext";
import DocumentVersionName from "../DocumentVersionName";
import type { RegestResult } from "../regest/regestParts";
import TextWithStatements from "../regest/TextWithStatements";
import { addedToDocument, isShownFirst, type AddedAnnotations, type AddedStatements, type AddedTarget } from "./addedItems";
import AnnotationSetEdit from "./AnnotationSetEdit";
import { documentActivity } from "./documentActivity";

interface DocumentWithAdditionsProps {
  projectId: string;
  document: SourceDocument;
}

/** A merged set open in the editor, on the version it opened on (Sol S4). */
interface OpenedSet {
  readonly reference: string;
  readonly name: string;
  readonly set: AnnotationSet;
}

/**
 * A document's text with what the project added to it (F84 D-02, F94, O-1), the Documents lens
 * on the data: the text once, part by part, with the spans of one annotation set (v1's span
 * view) and, beside each part, the statements accepted on it. A statement and its words lead to
 * each other: pointing at either highlights both, clicking keeps them. What is accepted shows;
 * proposals and closed results wait behind one toggle. Nothing is decided here: a proposal is
 * accepted in its review step.
 */
export default function DocumentWithAdditions({ projectId, document }: DocumentWithAdditionsProps) {
  const { t } = useTranslation();
  const shown = formatDocumentVersionRef(document.ref);
  const { documentId } = document.ref;
  const load = useCallback(async (signal: AbortSignal) => {
    const [sets, results, accepted] = await Promise.all([
      listDocumentAnnotationSets(projectId, documentId, signal),
      listDocumentResults(projectId, documentId, signal),
      listAcceptedStatements(projectId, shown, signal),
    ]);
    return { ...addedToDocument(sets, results), accepted: accepted.map((result): RegestResult => ({ ...result, accepted: true })) };
  }, [projectId, documentId, shown]);
  const { state, reload, refresh } = useLoad(load);
  // A Thread about this document moved (a render, an acceptance): read what it shows again.
  const activity = documentActivity(useLiveProject(projectId, THREAD_LIST_LIMIT)?.threads ?? null, documentId);
  const seenActivity = useRef(activity);
  useEffect(() => {
    if (activity === seenActivity.current) return;
    seenActivity.current = activity;
    refresh();
  }, [activity, refresh]);
  const [showAll, setShowAll] = useState(false);
  const [chosenSet, setChosenSet] = useState<string | null>(null);
  const [opened, setOpened] = useState<OpenedSet | null>(null);
  const projects = useProjects();
  const mayPropose = isAllowed(chosenProjectDecision(projects, "propose_change"));

  const loaded = state.status === "loaded" ? state.value : null;
  const annotations = loaded?.annotations ?? NO_ANNOTATIONS;
  // A set on another version of the document can't be drawn on this text; it is listed instead.
  const { drawable, chosen } = useMemo(() => {
    const onThisVersion = annotations.filter((item) => item.set.document === shown && (showAll || isShownFirst(item.state)));
    return { drawable: onThisVersion, chosen: onThisVersion.find((item) => item.identity === chosenSet) ?? onThisVersion[0] ?? null };
  }, [annotations, shown, showAll, chosenSet]);
  const labels = useLabelListNames(chosen?.set.label_list ?? null);
  const drawn = useMemo(
    () => (chosen === null ? null : {
      annotations: groupByDocument(chosen.set).get(shown) ?? [],
      labelNames: labels?.labels ?? NO_LABEL_NAMES,
      labelOptions: labels?.options ?? NO_LABEL_OPTIONS,
    }),
    [chosen, shown, labels],
  );
  const accepted = loaded?.accepted ?? NO_ACCEPTED;

  if (opened !== null) {
    // Chat asks about the version on screen: while editing, the one the editor opened on (Sol S6).
    const subject = { reference: opened.reference, name: t("chatReferences.annotations") };
    return (
      <SelectionToChat projectId={projectId} subject={subject}>
        <div className="space-y-3">
          <AskInChatButton projectId={projectId} subject={subject} />
          <AnnotationSetEdit projectId={projectId} {...opened} onClose={() => { setOpened(null); }} />
        </div>
      </SelectionToChat>
    );
  }

  const inRegest = new Set(accepted.map((result) => result.source));
  const statements = (loaded?.statements ?? NO_STATEMENTS).filter((item) => !inRegest.has(item.source));
  const waiting = [...annotations, ...statements].filter((item) => !isShownFirst(item.state)).length;
  const elsewhere = annotations.filter((item) => item.set.document !== shown && (showAll || isShownFirst(item.state)));
  const otherResults = statements.filter((item) => showAll || isShownFirst(item.state));
  const target = chosen?.target ?? null;
  const editable = target?.kind === "item" && mayPropose ? target : null;
  const resultSources = [...inRegest];
  return (
    <div className="space-y-2">
      {state.status === "loaded" ? null : <LoadStatus state={state} loadingLabel={t("sources.document.added.loading")} onRetry={reload} compact />}
      {chosen === null && resultSources.length === 0 && waiting === 0 ? null : (
        // The results' links stand over the statements' column where there is one, else beside the set's actions (R1-04).
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm pane-lg:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            {chosen === null ? null : (
              <>
                {drawable.length > 1 ? (
                  <select className={`${FORM_SELECT_CLASS} !w-auto`} aria-label={t("sources.document.added.annotations")} value={chosen.identity} onChange={(event) => { setChosenSet(event.target.value); }}>
                    {drawable.map((item) => <option key={item.identity} value={item.identity}>{setName(item, t)}</option>)}
                  </select>
                ) : <span className="app-label text-xs">{t("sources.document.added.annotations")}</span>}
                {isShownFirst(chosen.state) ? null : <CurationStateBadge state={chosen.state} />}
                <AskInChatButton projectId={projectId} subject={{ reference: chosen.key, name: t("chatReferences.annotations") }} />
                {editable === null ? null : (
                  <AppButton size="xs" variant="ghost" onClick={() => { setOpened({ reference: editable.reference, name: editable.name, set: chosen.set }); }}>{t("sources.document.added.edit")}</AppButton>
                )}
                <TargetLink target={chosen.target} />
                {chosen.target.kind === "result" ? <OpenReview source={chosen.target.source} state={chosen.state} review={chosen.target.review} /> : null}
              </>
            )}
            {waiting === 0 ? null : (
              <AppButton size="xs" variant="ghost" aria-pressed={showAll} onClick={() => { setShowAll(!showAll); }}>
                {t(showAll ? "sources.document.added.hideOthers" : "sources.document.added.showOthers", { count: waiting })}
              </AppButton>
            )}
          </div>
          {resultSources.length > 0 ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="app-label text-xs">{t("sources.document.added.statements")}</span>
              {resultSources.map((source, index) => (
                <WorkspaceLink key={source} route={proposalPath(source)} placement="beside" className="app-link text-xs" title={t("sources.document.added.openResultHint")}>
                  {resultSources.length === 1 ? t("sources.document.added.openResult") : t("sources.document.added.openResultNumbered", { number: index + 1 })}
                </WorkspaceLink>
              ))}
            </div>
          ) : null}
        </div>
      )}
      {elsewhere.length === 0 && otherResults.length === 0 ? null : (
        <ul className="space-y-1 text-sm">
          {elsewhere.map((item) => <ElsewhereItem key={item.identity} item={item} />)}
          {otherResults.map((item) => <StatementsItem key={item.source} item={item} shown={shown} />)}
        </ul>
      )}
      <TextWithStatements
        document={document}
        project={projectId}
        results={accepted}
        annotations={drawn}
        askInProject={projectId}
        askWith={chosen === null ? null : { reference: chosen.key, name: t("chatReferences.annotations") }}
      />
    </div>
  );
}

const NO_ANNOTATIONS: readonly AddedAnnotations[] = [];
const NO_STATEMENTS: readonly AddedStatements[] = [];
const NO_ACCEPTED: readonly RegestResult[] = [];
const NO_LABEL_NAMES: ReadonlyMap<string, string> = new Map();
const NO_LABEL_OPTIONS: AnnotationLabelOption[] = [];

/** A set in the chooser: its name, and its state unless it is accepted. */
function setName(item: AddedAnnotations, t: (key: string) => string): string {
  const name = item.target.kind === "item" ? item.target.name : t("chatReferences.annotations");
  return isShownFirst(item.state) ? name : `${name} · ${t(`data.states.${item.state}`)}`;
}

/** A set on another version of the document: v1 marked it so; its name opens that version. */
function ElsewhereItem({ item }: { item: AddedAnnotations }) {
  const { t } = useTranslation();
  return (
    <li className="flex flex-wrap items-center gap-2">
      {isShownFirst(item.state) ? null : <CurationStateBadge state={item.state} />}
      <span className="app-label text-xs">{t("sources.document.added.annotations")}</span>
      <span className="app-text-muted text-xs">{t("sources.document.added.otherVersion")} <DocumentVersionName reference={item.set.document} /></span>
      <TargetLink target={item.target} />
    </li>
  );
}

function StatementsItem({ item, shown }: { item: AddedStatements; shown: string }) {
  const { t, i18n } = useTranslation();
  return (
    <li className="flex flex-wrap items-center gap-2">
      <CurationStateBadge state={item.state} />
      <WorkspaceLink route={proposalPath(item.source)} placement="beside" className="app-link">
        {t("sources.document.added.statementCount", { count: item.statements })}
      </WorkspaceLink>
      {item.document === shown ? null : (
        <span className="app-text-muted text-xs">{t("sources.document.added.otherVersion")} <DocumentVersionName reference={item.document} /></span>
      )}
      <span className="app-text-muted text-xs">{formatTimeAgo(item.recordedAt, i18n.resolvedLanguage)}</span>
      <OpenReview source={item.source} state={item.state} review={item.review} />
    </li>
  );
}

function TargetLink({ target }: { target: AddedTarget }) {
  const { t } = useTranslation();
  switch (target.kind) {
    case "result":
      return <WorkspaceLink route={proposalPath(target.source)} placement="beside" className="app-link text-xs" title={t("sources.document.added.openResultHint")}>{t("sources.document.added.openResult")}</WorkspaceLink>;
    case "review":
      return (
        <>
          <Link className="app-link text-xs" to={appPaths.review(target.changeSetId)}>{t("sources.document.added.openReview")}</Link>
          {target.origin === null ? null : <Link className="app-link text-xs" to={threadEntryPath(target.origin)}>{t("sources.document.added.openThread")}</Link>}
        </>
      );
    case "item":
      return <Link className="app-link text-xs" to={appPaths.item(target.reference)} title={t("sources.document.added.openItemHint")}>{t("sources.document.added.openItem")}</Link>;
  }
}

function threadEntryPath(entry: string): string {
  const [threadId = "", number = ""] = entry.split("#");
  return appPaths.thread(threadId, Number(number));
}
